// React.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery, buildApiQuery } from "../../../helpers/timeRangeUtils";

import { Column, usePagination, useSortBy, useTable } from 'react-table';
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import { SqlStatisticsSummary } from '../../../types/instance/SqlStatistics';

// Components.
import Alert from "../../Alert";
import NoData from "../../NoData";
import TablePagination from "../../TablePagination";

// Helper.
import Helper from '../../../helpers/Helper';
import {
    capitalizeString,
    fetchResults,
    getDefaultPageSize, getToolTipPosition,
} from '../../../helpers/utils';
import SqlApi from '../../../api/instance/SqlApi';
import { showError } from '../../../utils/showAlert';

// Constants.
import StatementToolTip from "./StatementToolTip";

// Constants.
import { INSTANCE_TYPE_COCKROACHDB } from "../../Constants";
import SearchInput from '../../SearchInput';
import GroupedViewToggle from "../GroupedViewToggle";

export default function SqlStatisticsTable(props:
                                           {
                                               instance: InstanceTarget,
                                               statementId?: string,
                                               isGroup?: boolean,
                                               onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement') => void
                                           }) {
    const isGroup = !!props.isGroup;
    const statementType = isGroup ? 'groupedstatement' : 'statement';
    const [sqlStatistics, setSqlStatistics] =
            useState<SqlStatisticsSummary[]>([]);
    const [supportedStatistics, setSupportedStatistics] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const isCockroachDb = props.instance.type === INSTANCE_TYPE_COCKROACHDB
    const hiddenColumnsList = isCockroachDb ? ['logical_writes', 'logical_reads', 'physical_reads', 'cpu_time_millisconds'] : []
    const timeRangeContext = useContext(TimeRangeContext)
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);


    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });


    // Table columns
    const columns: Column[] = useMemo(() => {
                const defaultColumns = [
                    {
                        Header: '#',
                        accessor: 'sqlhash',
                        headerClassName: 'text sql-statistics-col-hash',
                        className: 'sql-statistics-col-hash',
                        canSort: true,
                        sortDescFirst: false,
                        sortType: 'basic',
                        Cell: ({ cell }: any) => {
                            // const hideTab = (props.instance.type === INSTANCE_TYPE_ORACLE || props.instance.type === INSTANCE_TYPE_DB2) ?  '' : '?hide=activity'
                            return (<Link
                                    to={isGroup
                                            ? `/instances/${props.instance.id}/activity/groupedstatement/${cell.row.values.sqlhash}/sql-statistics?${buildRedirectQuery(timeRangeContext.timeRange)}`
                                            : `/instances/${props.instance.id}/sql-statistics/statement/${cell.row.values.sqlhash}/sql-statistics?${buildRedirectQuery(timeRangeContext.timeRange)}`}>
                                <i className="fal fa-arrow-up-right-from-square fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in new page"/> {cell.row.values.sqlhash}
                            </Link>)
                        }
                    },
                    {
                        Header: 'Statement',
                        accessor: 'sqltext',
                        headerClassName: 'text sql-statistics-col-statement',
                        canSort: true,
                        sortDescFirst: false,
                        className: 'concatenate-small col-width-25',
                        sortType: 'basic',
                        Cell: ({ cell }: any) => cell.row.values.sqltext ? (
                                props.onStatementClick ? (
                                        <span
                                                className="panel-link"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => props.onStatementClick!(cell.row.values.sqlhash, statementType)}
                                        >
                                <i className="fal fa-sidebar-flip fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in side panel"/>
                                            {cell.row.values.sqltext}
                            </span>
                                ) : (
                                        <Link
                                                to={isGroup
                                                        ? `/instances/${props.instance.id}/activity/groupedstatement/${cell.row.values.sqlhash}/text?${buildRedirectQuery(timeRangeContext.timeRange)}`
                                                        : `/instances/${props.instance.id}/sql-statistics/statement/${cell.row.values.sqlhash}/text?${buildRedirectQuery(timeRangeContext.timeRange)}`}
                                        >
                                            <i className="fal fa-sidebar-flip fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in side panel"/>
                                            {cell.row.values.sqltext}
                                        </Link>
                                )
                        ) : <React.Fragment>-</React.Fragment>

                    },
                    {
                        Header: 'Duration',
                        accessor: 'duration',
                        headerClassName: 'text-end sql-statistics-col-duration',
                        className: 'text-end sql-statistics-col-duration',
                        canSort: true,
                        sortDescFirst: true,
                        sortType: 'basic',
                        Cell: ({ cell }: any) =>
                                (Helper.getTimeInEnglish(cell.row.values.duration))
                    },
                    {
                        Header: 'Executions',
                        accessor: 'executions',
                        headerClassName: 'text-end sql-statistics-col-executions',
                        className: 'text-end sql-statistics-col-executions',
                        canSort: true,
                        sortDescFirst: true,
                        sortType: 'basic',
                        Cell: ({ cell }: any) =>
                                (Helper.getFormattedNumber(cell.row.values.executions))
                    },
                    {
                        Header: 'Avg. Duration',
                        accessor: 'average_duration',
                        headerClassName: 'text-end sql-statistics-col-avg-duration',
                        className: 'text-end sql-statistics-col-avg-duration',
                        canSort: true,
                        sortType: 'basic',
                        sortDescFirst: true,
                        Cell: ({ cell }: any) => cell.row.values.average_duration ? Helper.getTimeInEnglish(cell.row.values.average_duration) : ''
                    },
                ]
                supportedStatistics.forEach(item => {
                    if (item !== 'executions' && item !== 'duration') {
                        defaultColumns.push({
                            Header: capitalizeString(item),
                            accessor: item,
                            headerClassName: 'text-end sql-statistics-extra-col',
                            className: 'text-end sql-statistics-extra-col',
                            canSort: true,
                            sortDescFirst: true,
                            sortType: 'basic',
                            Cell: ({ cell }) => {
                                const key = cell.column.id;
                                const value = cell.row.values[key];

                                const isTime = key.includes('time');

                                return value
                                        ? isTime
                                                ? Helper.getTimeInEnglish(value)
                                                : Number(value).toFixed(2).replace(/\.?0+$/, '')
                                        : '0';
                            }
                        })
                    }
                })
                return defaultColumns
            },
            [props.instance.id, props.instance.type, supportedStatistics, isGroup]);

    // Table data
    useEffect(() => {
                async function load() {
                    try {
                        setLoading(true);
                        setSqlStatistics([]);
                        const fetchStatistics = isGroup ? SqlApi.fetchStatisticGroup : SqlApi.fetchStatisticSql;
                        const results: any[][] = await fetchResults(
                                [
                                    fetchStatistics(buildApiQuery(timeRangeContext.timeRange), props.instance.id, props.instance.type, '&sort=duration+desc')
                                ]);
                        const rows = isGroup ? results[0].map((item: SqlStatisticsSummary) => ({ ...item, sqlhash: item.grouphash })) : results[0];

                        const supportedStatistics: any[][] = await fetchResults(
                                [
                                    SqlApi.fetchStatisticName(props.instance.type, '&collect=true')
                                ]);

                        if (active) {
                            const resultsWithAverage = Helper.addAverageAndColor('duration', 'executions', rows, 0)
                            setSupportedStatistics(supportedStatistics[0]);
                            const filteredList = props.statementId ? resultsWithAverage.filter(item => item.sqlhash === props.statementId) : resultsWithAverage
                            setSqlStatistics(filteredList);
                            setLoading(false);
                        }
                    } catch (x: any) {
                        console.log(x.message);
                        const errorMessage = x.message || x.toString();
                        setError(errorMessage);
                        showError('Failed to load SQL statistics', errorMessage);
                        setLoading(false);
                        setSqlStatistics([]);
                    }
                }

                let active: boolean = true;
                // console.log ('Entering useEffect', active);
                // load returns before the results are available...
                load();
                // console.log ('Leaving useEffect', active);
                return () => {
                    // Clean up afterwards to avoid race conditions.
                    // console.log ('Clean up');
                    active = false;
                }
            },
            [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instance.id, props.instance.type,
                props.statementId, isGroup]);

    const data = useMemo(() =>
                    sqlStatistics.filter((row) =>
                            row.sqltext.toLowerCase().includes(tableFilter.toLowerCase()) ||
                            // @ts-ignore
                            row.sqlhash.toLowerCase().includes(tableFilter.toLowerCase())),
            [sqlStatistics, tableFilter]);

    // Transform function to escape double quotes in SQL text for CSV export
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        const transformed = JSON.parse(JSON.stringify(csvData));
        transformed.forEach((item: any) => {
            if (item.sqltext) {
                item.sqltext = item.sqltext.replaceAll('"', '""');
            }
        });
        return transformed;
    }, []);

    const tableInstance = useTable(
            {
                columns,
                data,
                initialState:
                        {
                            hiddenColumns: hiddenColumnsList,
                            pageIndex: initialPageIndex,
                            pageSize: initialPageSize,
                        },
                disableSortRemove: true,
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            }, useSortBy, usePagination);

    const
            {
                getTableProps,
                getTableBodyProps,
                headerGroups,
                prepareRow,
                page,
                setPageSize,
                pageOptions,
                pageCount,
                canPreviousPage,
                previousPage,
                canNextPage,
                nextPage,
                gotoPage,
                state: { pageIndex, pageSize }
            } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    function clearTableFilter() {
        setTableFilter('');
    }

    // The bracket must be present and on the same line as the return.
    return (
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
                    {isGroup ? 'Grouped Statements' : 'Statements'}
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseStatements" aria-expanded="false" aria-controls="collapseStatements"/>
                    {!props.statementId && (
                            <GroupedViewToggle section="sql-statistics" instanceId={props.instance.id} isGroup={isGroup}/>
                    )}
                </div>
                <div id="collapseStatements" className="card-body collapse show">
                    {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 &&
                            (<Alert
                                    message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by the number of executions.`}
                                    heading="Statement records" variant="alert-info"/>)}
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10">
                            <TableCSVExport
                                    tableInstance={tableInstance}
                                    filename="SQL Statistics"
                                    transformData={transformCsvData}
                            />
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                    ref={inputRef}
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                            />
                        </div>
                    </div>
                    <div className="table-responsive statements-table sql-statistics-table-wrapper">
                        <table {...getTableProps()} className="table statements-table sql-statistics-table">
                            <thead>
                            {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                                <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                    className={(column as any).headerClassName}>
                                                    {column.render('Header')}
                                                    {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                                    <i className="fal fa-sort-amount-up-alt"></i> :
                                                                    <i className="fal fa-sort-amount-down-alt"></i>) :
                                                            <i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
                                                </th>))}
                                    </tr>))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {page.map((row) => {
                                prepareRow(row);
                                return (
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map((cell) => {
                                                return (
                                                        <td {...cell.getCellProps()}
                                                            className={(cell.column as any).className}>

                                                            {cell.column.id === "sqltext" ?
                                                                    <div className='tooltip-scroll ellipsis'
                                                                         onMouseEnter={(event) => setVisibleToolTip({
                                                                             id: cell.row.values.sqlhash,
                                                                             position: getToolTipPosition(event)
                                                                         })}
                                                                         onMouseLeave={() => setVisibleToolTip({
                                                                             id: '',
                                                                             position: 0
                                                                         })}
                                                                    >
                                                                        {cell.render('Cell')}
                                                                        {(cell.row.values.sqlhash === visibleToolTip.id &&
                                                                            <StatementToolTip instanceType={props.instance.type}
                                                                                              position={visibleToolTip.position}
                                                                                              id={cell.row.values.sqlhash}
                                                                                              onClick={props.onStatementClick ? () => props.onStatementClick!(cell.row.values.sqlhash, statementType) : undefined}
                                                                                              link={props.onStatementClick ? undefined : `/instances/${props.instance.id}/activity/${statementType}/${cell.row.values.sqlhash}/text`}
                                                                                              cell={cell}/>
                                                                        )}
                                                                    </div> : cell.render('Cell')}

                                                        </td>)
                                            })}
                                        </tr>)
                            })}
                            </tbody>
                        </table>
                    </div>
                    <NoData error={error} loading={loading}
                            length={data.length}/>
                    <TablePagination
                            length={data.length}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            canNextPage={canNextPage}
                            nextPage={nextPage}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                    />
                </div>
            </div>
    )
}
